import { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, Ban, TrendingDown, Ticket } from 'lucide-react';
import type { AlertItem } from '../../../api/marketing';
import { fetchAlerts } from '../../../api/marketing';

const SEVERITY_CONFIG = {
  critical: {
    icon: Ban,
    border: 'border-red-200 dark:border-red-800',
    bg: 'bg-red-50 dark:bg-red-900/20',
    iconColor: 'text-red-500',
    label: 'Critical',
    labelColor: 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30',
  },
  warning: {
    icon: AlertTriangle,
    border: 'border-amber-200 dark:border-amber-800',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    iconColor: 'text-amber-500',
    label: 'Warning',
    labelColor: 'text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  },
  info: {
    icon: Info,
    border: 'border-blue-200 dark:border-blue-800',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    iconColor: 'text-blue-500',
    label: 'Info',
    labelColor: 'text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30',
  },
};

const TYPE_ICONS = {
  wasted_spend: Ban,
  missed_opportunity: Ticket,
  declining_roas: TrendingDown,
  low_fill_rate: AlertCircle,
};

export default function OptimizationAlerts() {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAlerts()
      .then((res) => setAlerts(res.alerts))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  const criticals = alerts.filter((a) => a.severity === 'critical');
  const warnings = alerts.filter((a) => a.severity === 'warning');
  const infos = alerts.filter((a) => a.severity === 'info');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-bark-900 dark:text-stone-100">Optimization Alerts</h2>
        <div className="flex items-center gap-3 text-xs text-stone-500">
          {criticals.length > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <Ban size={12} /> {criticals.length} critical
            </span>
          )}
          {warnings.length > 0 && (
            <span className="flex items-center gap-1 text-amber-600">
              <AlertTriangle size={12} /> {warnings.length} warnings
            </span>
          )}
          {infos.length > 0 && (
            <span className="flex items-center gap-1 text-blue-600">
              <Info size={12} /> {infos.length} info
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-20" />
          ))}
        </div>
      ) : alerts.length === 0 ? (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
          <div className="text-green-500 mx-auto mb-2 text-2xl">&#10003;</div>
          <p className="text-sm text-green-700 dark:text-green-400 font-medium">All Clear</p>
          <p className="text-xs text-green-600 dark:text-green-500 mt-1">
            No optimization alerts right now. Marketing spend and ticket availability are aligned.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Critical */}
          {criticals.length > 0 && (
            <AlertGroup title="Critical" alerts={criticals} />
          )}
          {/* Warnings */}
          {warnings.length > 0 && (
            <AlertGroup title="Warnings" alerts={warnings} />
          )}
          {/* Info */}
          {infos.length > 0 && (
            <AlertGroup title="Informational" alerts={infos} />
          )}
        </div>
      )}
    </div>
  );
}

function AlertGroup({ title, alerts }: { title: string; alerts: AlertItem[] }) {
  return (
    <div>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-2">
        {title}
      </h3>
      <div className="space-y-2">
        {alerts.map((alert, i) => {
          const config = SEVERITY_CONFIG[alert.severity];
          const TypeIcon = TYPE_ICONS[alert.type] || AlertCircle;

          return (
            <div
              key={`${alert.type}-${i}`}
              className={`rounded-lg border p-4 ${config.border} ${config.bg}`}
            >
              <div className="flex items-start gap-3">
                <TypeIcon className={`${config.iconColor} flex-shrink-0 mt-0.5`} size={18} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-bark-900 dark:text-stone-100">{alert.title}</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${config.labelColor}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className="text-xs text-stone-600 dark:text-stone-400 mt-1">{alert.description}</p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-stone-500 dark:text-stone-400">
                    {alert.region && <span>Region: <strong>{alert.region}</strong></span>}
                    {alert.campaignName && <span>Campaign: <strong>{alert.campaignName}</strong></span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
